import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { loginWithEmail }    from '../lib/auth'
import { useAuth }           from '../lib/useAuth'
import { loginWithApple, isAppleSignInConfigured } from '../lib/appleSignIn'
import { useT } from '../i18n'

export function Login() {
  const nav = useNavigate()
  const { setAuthUser } = useAuth()
  const t = useT()
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [busy,     setBusy]     = useState(false)
  const [appleBusy, setAppleBusy] = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const u = await loginWithEmail(email.trim(), password)
      setAuthUser(u)
      nav('/app', { replace: true })
    } catch (err: any) {
      setError(err?.message ?? 'Login fehlgeschlagen.')
    } finally {
      setBusy(false)
    }
  }

  const handleApple = async () => {
    setAppleBusy(true); setError(null)
    try {
      const u = await loginWithApple()
      setAuthUser(u)
      nav('/app', { replace: true })
    } catch (err: any) {
      // Popup geschlossen → kein Fehler anzeigen
      const msg = err?.message ?? ''
      if (/popup_closed|popup-closed|cancel/i.test(msg)) {
        setAppleBusy(false)
        return
      }
      setError(msg || 'Apple-Anmeldung fehlgeschlagen.')
    } finally {
      setAppleBusy(false)
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center px-6 py-12">
      <div className="glass-strong w-full max-w-md p-8">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block text-accent text-sm hover:opacity-80">{t('auth.backToOverview')}</Link>
          <h1 className="mt-3 text-3xl font-bold">{t('auth.login')}</h1>
          <p className="mt-2 text-sm text-app-text-dim">{t('auth.signInPrompt')}</p>
        </div>

        {isAppleSignInConfigured ? (
          <button
            type="button"
            onClick={handleApple}
            disabled={appleBusy || busy}
            className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl
                       bg-black text-white font-semibold text-sm
                       hover:bg-black/85 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AppleLogoBlack />
            {appleBusy ? '…' : t('auth.appleSignIn')}
          </button>
        ) : (
          <div className="w-full px-4 py-3 rounded-xl bg-app-elev border border-app-border
                          text-sm text-app-text-mute text-center">
            <span className="inline-flex items-center gap-2">
              <span></span>
              {t('auth.appleSignIn')}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/20 border border-accent/30 text-accent font-bold tracking-wider ml-1">
                BALD
              </span>
            </span>
            <div className="mt-1 text-[10px] text-app-text-mute">{t('auth.appleSoon')}</div>
          </div>
        )}

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-app-border" />
          <span className="text-[10px] font-bold text-app-text-mute tracking-wider">{t('auth.orWithEmail')}</span>
          <div className="flex-1 h-px bg-app-border" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label={t('auth.email')} type="email" value={email} onChange={setEmail} autoComplete="email" required />
          <Field label={t('auth.password')} type="password" value={password} onChange={setPassword} autoComplete="current-password" required />

          {error && (
            <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || appleBusy || !email || !password}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? '…' : t('auth.login')}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-app-text-mute">
          {t('auth.noAccount')} {t('auth.registerHint')}
        </div>
      </div>
    </div>
  )
}

function Field(props: {
  label: string
  type?: string
  value: string
  onChange: (s: string) => void
  autoComplete?: string
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-app-text-dim mb-2">{props.label.toUpperCase()}</span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        autoComplete={props.autoComplete}
        required={props.required}
        className="w-full px-4 py-3 rounded-xl bg-app-input border border-app-border
                   text-app-text placeholder:text-app-text-mute focus:outline-none focus:border-accent
                   focus:ring-2 focus:ring-accent/30 transition"
      />
    </label>
  )
}

function AppleLogoBlack() {
  return (
    <svg width="16" height="18" viewBox="0 0 14 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.6 8.5c0-1.7 1.4-2.5 1.5-2.6-.8-1.2-2.1-1.4-2.5-1.4-1.1-.1-2.1.6-2.7.6S6.6 4.4 5.6 4.5c-1.2 0-2.3.7-2.9 1.7-1.2 2.2-.3 5.4.9 7.2.6.9 1.3 1.8 2.2 1.8.9 0 1.2-.6 2.3-.6s1.4.6 2.3.6c1 0 1.6-.9 2.2-1.8.7-1 1-2.1 1-2.1 0 0-1.9-.7-2-2.8zM9.7 3c.5-.6.8-1.4.7-2.3-.7 0-1.6.5-2.1 1.1-.4.5-.8 1.4-.7 2.2.8.1 1.6-.4 2.1-1z"/>
    </svg>
  )
}
